import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Monitor, Phone, RefreshCw, Smartphone } from 'lucide-react';
import Seo from '../components/Seo';
import CompanyField, { publicCompanySearch } from '../components/CompanyField';
import ContractSign, { emptySignature, signatureProblems } from '../components/ContractSign';
import { CLOUD_PBX } from '../data/cloudPbx';
import { portalUrl } from '../config/site';
import { capturePartner, currentPartner } from '../config/partnerRef';
import { vatNote } from '../config/vat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PLANS = CLOUD_PBX.pricing.plans;
const PHONE_APP_URL = 'https://app.voip2.co.uk';
const CUSTOMER_PORTAL_URL = portalUrl('/customer/login').startsWith('http') ? portalUrl('/customer/login') : 'https://portal.voip2.co.uk/customer/login';
// Same tiers as the backend (services/pbx_billing.py): plan fee discount by number of users; over 50 is quoted.
const VOLUME_DISCOUNTS = [[40, 15], [25, 10], [10, 5]];
const STEPS = ['Your phone system', 'Choose your numbers', 'Your phones', 'Your details'];
const money = (pounds) => `£${pounds.toFixed(2)}`;
const pretty = (n) => (n.startsWith('020') ? `${n.slice(0, 3)} ${n.slice(3, 7)} ${n.slice(7)}`
  : n.length === 11 ? `${n.slice(0, 4)} ${n.slice(4, 7)} ${n.slice(7)}` : n);

const Panel = ({ children }) => <div className="rounded-xl border border-gray-200 bg-white">{children}</div>;

/**
 * Website signup for the VOIP2 Phone System: start a 14-day free trial or order straight away.
 * Steps follow the usual UK provider flow: plan and number of phones, real free numbers (national 03 or local
 * 01/02), app or desk phone for each user, then contact details.
 */
const FreeTrial = () => {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get('mode') === 'buy' ? 'buy' : 'trial');
  const [step, setStep] = useState(0);
  const [plan, setPlan] = useState(PLANS.find((p) => p.name === params.get('plan')) ? params.get('plan') : 'Business');
  const [users, setUsers] = useState(3);
  const [options, setOptions] = useState(null);
  const [tab, setTab] = useState('national');
  const [area, setArea] = useState('0203');
  const [numbers, setNumbers] = useState({ list: [], loading: false, available: true });
  const [chosen, setChosen] = useState([]);
  const [migrate, setMigrate] = useState(false);
  const [portNumber, setPortNumber] = useState('');
  const [phones, setPhones] = useState([]);
  const [form, setForm] = useState({ first_name: '', last_name: '', company: '', company_number: null, email: '', phone: '', accept_terms: false, website: '' });
  const [signature, setSignature] = useState(emptySignature());
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [partner, setPartner] = useState(null);

  const trial = mode === 'trial';
  const maxUsers = trial ? (options?.trial.max_users || 5) : 50;
  const maxNumbers = trial ? (options?.trial.max_numbers || 1) : (options?.buy.max_numbers || 5);

  useEffect(() => {
    capturePartner();
    const code = currentPartner();
    if (code) fetch(`${BACKEND_URL}/api/trials/partner/${code}`).then((r) => (r.ok ? r.json() : null)).then(setPartner).catch(() => {});
  }, []);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/trials/options`).then((r) => r.json()).then(setOptions).catch(() => setOptions({ areas: [], phones: [], trial: { max_users: 5, max_numbers: 1 }, buy: { max_numbers: 5 } }));
  }, []);
  useEffect(() => { if (users > maxUsers) setUsers(maxUsers); }, [maxUsers, users]);
  useEffect(() => { if (chosen.length > maxNumbers) setChosen(chosen.slice(0, maxNumbers)); }, [maxNumbers, chosen]);
  useEffect(() => {
    setPhones((prev) => Array.from({ length: users }, (_, i) => prev[i] || { kind: 'app', product_id: null }));
  }, [users]);

  const search = useCallback(() => {
    setNumbers((n) => ({ ...n, loading: true }));
    const q = tab === 'national' ? 'kind=national' : `kind=local&area=${encodeURIComponent(area)}`;
    fetch(`${BACKEND_URL}/api/trials/numbers?${q}`)
      .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Search failed'); return d; })
      .then((d) => setNumbers({ list: d.numbers, loading: false, available: d.available }))
      .catch((e) => { setNumbers({ list: [], loading: false, available: true }); setErrors([e.message]); });
  }, [tab, area]);
  useEffect(() => { if (step === 1) search(); }, [step, search]);

  const planInfo = PLANS.find((p) => p.name === plan);
  const deskPhones = phones.filter((p) => p.kind === 'desk' && p.product_id);
  const hardwareTotal = deskPhones.reduce((sum, p) => sum + ((options?.phones.find((x) => x.id === p.product_id)?.price_pence || 0) / 100), 0);
  const extraNumbers = Math.max(0, chosen.length - 1);
  const extraPrice = (options?.extra_number_monthly_pence || 0) / 100;
  const discountPct = trial ? 0 : (VOLUME_DISCOUNTS.find(([min]) => users >= min) || [0, 0])[1];
  // A reseller sets their own price per user (from /api/trials/partner); otherwise our list price.
  const unitPrice = (p) => (p ? (partner?.prices_pence?.[p.name] ?? Math.round(p.monthly * 100)) / 100 : 0);
  const listPrice = planInfo ? unitPrice(planInfo) * users : 0;
  const discount = Math.round(listPrice * discountPct) / 100;
  const monthly = useMemo(() => listPrice - discount + extraNumbers * extraPrice, [listPrice, discount, extraNumbers, extraPrice]);

  const toggleNumber = (n) => setChosen((c) => (c.includes(n) ? c.filter((x) => x !== n) : c.length >= maxNumbers ? (maxNumbers === 1 ? [n] : c) : [...c, n]));
  const setPhone = (i, patch) => setPhones((list) => list.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  const validate = () => {
    const e = [];
    if (step === 1) {
      if (!chosen.length && !migrate) e.push('Choose a phone number, or tick the box to bring your existing number.');
      if (migrate && !/^[0-9 +()-]{6,30}$/.test(portNumber.trim())) e.push('Enter the number you want to bring to us.');
    }
    if (step === 2 && phones.some((p) => p.kind === 'desk' && !p.product_id)) e.push('Choose a model for each desk phone.');
    if (step === 3) {
      if (!form.first_name.trim() || !form.last_name.trim()) e.push('Enter your name.');
      if (!form.company.trim()) e.push('Enter your company name.');
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.push('Enter a valid email address.');
      if (!/^[0-9 +()-]{6,30}$/.test(form.phone)) e.push('Enter a contact phone number.');
      if (!form.accept_terms) e.push(`Please accept the ${trial ? 'trial ' : ''}terms.`);
      signatureProblems(signature).forEach((x) => e.push(`Agreement: ${x}`));
    }
    setErrors(e);
    return e.length === 0;
  };
  const next = () => { if (validate()) { setErrors([]); setStep(step + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/trials/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form, mode, plan, users, chosen_numbers: chosen, partner: partner?.code || null, signature,
          number_type: chosen.length ? (chosen[0].startsWith('03') ? 'national' : 'local') : 'port',
          port_number: migrate ? portNumber.trim() : null,
          phones: phones.map((p) => ({ kind: p.kind, product_id: p.kind === 'desk' ? p.product_id : null })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = Array.isArray(data.detail) ? data.detail.map((x) => (x.msg || '').replace(/^Value error, /, '')).join(' ') : data.detail;
        throw new Error(d || 'Something went wrong. Please try again.');
      }
      setDone(data);
    } catch (e) { setErrors([e.message]); } finally { setBusy(false); }
  };

  const input = (key, label, type = 'text', extra = {}) => (
    <label className="block text-sm font-medium text-gray-700">{label}
      <input type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-[#0a1f2e] focus:outline-none" {...extra} />
    </label>
  );

  return (
    <div className="min-h-screen bg-[#0a1f2e] pt-24 pb-16 px-4">
      <Seo title={trial ? 'Cloud Phone System 14-Day Free Trial' : 'Get Started with the VOIP2 Phone System'}
        description="Set up your VOIP2 cloud phone system in minutes: choose your plan, numbers and phones." path="/free-trial" />
      <div className="mx-auto max-w-6xl lg:flex lg:items-start lg:gap-0">
        {/* Left: summary */}
        <aside className="mb-6 rounded-xl bg-[#0f2a3d] p-6 text-white lg:mt-6 lg:mb-0 lg:w-80 lg:shrink-0 lg:rounded-r-none">
          <div className="font-display text-2xl font-semibold">VOIP<span className="text-[#c8ff00]">2</span></div>
          {!done && <h1 className="sr-only">{trial ? 'Start your free trial of the VOIP2 cloud phone system' : 'Set up your VOIP2 cloud phone system'}</h1>}
          <p className="mt-6 text-lg"><b>Hello.</b> Let's get your new business phone system up and running.</p>
          {partner && <p className="mt-3 inline-block rounded-full bg-white/10 px-3 py-1 text-xs text-white/80">Through our partner {partner.name}</p>}
          <p className="mt-2 text-sm text-white/60">It only takes a few minutes{trial ? ', and the first 14 days are free' : ''}.</p>
          <div className="mt-6 border-t border-white/10 pt-5">
            <div className="text-sm text-white/70">{trial ? 'After your free trial' : 'Your monthly cost'}</div>
            <div className="text-3xl font-semibold">{money(monthly)}<span className="text-sm font-normal text-white/60"> /month</span></div>
            <ul className="mt-3 space-y-1 text-sm text-white/70">
              <li>{users} × {plan} @ {money(unitPrice(planInfo))}</li>
              {discountPct > 0 && <li className="text-[#c8ff00]">{discountPct}% volume discount: -{money(discount)}</li>}
              {extraNumbers > 0 && <li>{extraNumbers} extra number{extraNumbers > 1 ? 's' : ''} @ {money(extraPrice)}</li>}
              <li>First number included</li>
              {trial && <li>Trial: 100 UK minutes in total</li>}
              {hardwareTotal > 0 && <li className="pt-2 text-white">Desk phones (one-off): {money(hardwareTotal)}</li>}
              <li className="pt-2 text-xs text-white/50">Prices: {vatNote()}. 24-month term after the free trial.</li>
            </ul>
            {trial && <div className="mt-4 rounded-lg bg-[#c8ff00] px-3 py-2 text-sm font-semibold text-[#0a1f2e]">Free for 14 days · no card needed</div>}
          </div>
          <div className="mt-6 border-t border-white/10 pt-5 text-sm text-white/70">
            Questions? <a className="text-[#c8ff00] underline" href="mailto:hello@voip2.co.uk">hello@voip2.co.uk</a>
          </div>
        </aside>

        {/* Right: steps */}
        <main className="flex-1 overflow-hidden rounded-xl bg-white shadow-2xl">
          <ol className="grid grid-cols-4 border-b bg-gray-50 text-xs sm:text-sm">
            {STEPS.map((s, i) => (
              <li key={s} className={`flex items-center gap-2 px-3 py-3 ${i === step ? 'bg-[#0a1f2e] text-white' : i < step ? 'text-[#0a1f2e]' : 'text-gray-400'}`}>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] ${i < step ? 'border-[#0a1f2e] bg-[#c8ff00] text-[#0a1f2e]' : 'border-current'}`}>
                  {i < step ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className="hidden sm:inline">{s}</span>
              </li>
            ))}
          </ol>

          <div className="p-6 sm:p-10">
            {done ? (
              <div className="py-10 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#c8ff00]"><Check className="h-7 w-7 text-[#0a1f2e]" /></div>
                <h1 className="mt-6 text-2xl font-semibold text-[#0a1f2e]">{done.status === 'active' ? 'Your trial is ready' : done.status === 'order_received' ? 'Thanks for your order' : 'Thanks, we have your request'}</h1>
                <p className="mx-auto mt-3 max-w-md text-gray-600">{done.message}</p>
                {done.status !== 'received' && (
                  <div className="mx-auto mt-8 grid max-w-2xl gap-4 text-left sm:grid-cols-2">
                    {done.status === 'active' && (
                      <div className="rounded-xl border border-gray-200 p-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-400">Step 1 · your phones</div>
                        <h3 className="mt-1 font-semibold text-[#0a1f2e]">Set up your phones and apps</h3>
                        <p className="mt-2 text-sm text-gray-600">Open the email <b>“Welcome to VOIP2 - set up your free trial”</b> and choose your password. Then sign in to the web phone or the mobile app.</p>
                        <a href={PHONE_APP_URL} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm font-semibold text-[#0a1f2e] underline">Phone system login: app.voip2.co.uk</a>
                      </div>
                    )}
                    <div className="rounded-xl border border-gray-200 p-5">
                      <div className="text-xs font-semibold uppercase tracking-wider text-gray-400">{done.status === 'active' ? 'Step 2 · your account' : 'Your account'}</div>
                      <h3 className="mt-1 font-semibold text-[#0a1f2e]">Activate your customer portal</h3>
                      <p className="mt-2 text-sm text-gray-600">Open the email <b>“Activate your VOIP2 customer portal”</b>. In the portal you see your numbers, users, calls and bills, and add a card to keep your service.</p>
                      <a href={CUSTOMER_PORTAL_URL} className="mt-3 inline-block text-sm font-semibold text-[#0a1f2e] underline">Customer portal: portal.voip2.co.uk</a>
                    </div>
                  </div>
                )}
                {done.status === 'active' && <p className="mx-auto mt-6 max-w-md text-xs text-gray-500">No email? Check your spam folder, or write to hello@voip2.co.uk. Desk phones are sent once you place an order.</p>}
                <Link to="/products/cloud-pbx" className="mt-8 inline-block text-sm text-[#0a1f2e] underline">Back to Phone System</Link>
              </div>
            ) : (
              <>
                {errors.length > 0 && (
                  <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                    <b>We've just got a small issue to fix before we can continue</b>
                    <ul className="mt-1 list-disc pl-5">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
                  </div>
                )}

                {step === 0 && (
                  <div className="space-y-8">
                    <div className="mx-auto flex w-fit rounded-full bg-gray-100 p-1 text-sm">
                      {[['trial', 'Free 14-day trial'], ['buy', 'Start my service now']].map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setMode(k)}
                          className={`rounded-full px-5 py-2 font-medium ${mode === k ? 'bg-[#0a1f2e] text-white' : 'text-gray-600'}`}>{l}</button>
                      ))}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {PLANS.map((p) => (
                        <button key={p.name} type="button" onClick={() => setPlan(p.name)}
                          className={`rounded-xl border-2 p-4 text-left transition ${plan === p.name ? 'border-[#0a1f2e] bg-[#0a1f2e]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`}>
                          <div className="flex items-center justify-between"><span className="font-semibold">{p.name}</span>{plan === p.name && <Check className="h-4 w-4" />}</div>
                          <div className="mt-1 text-2xl font-semibold">{money(unitPrice(p))}<span className="text-xs font-normal text-gray-500">/user/month</span></div>
                          <div className="mt-1 text-xs text-gray-500">{p.tagline}</div>
                        </button>
                      ))}
                    </div>
                    <div className="text-center">
                      <h2 className="text-xl font-semibold text-[#0a1f2e]">How many phones does your business need?</h2>
                      <p className="mx-auto mt-2 max-w-lg text-sm text-gray-500">Desk phones, or apps on your computer and mobile. You can add more later{trial ? `; the trial is for up to ${maxUsers}` : ''}.</p>
                      <div className="mx-auto mt-8 max-w-md">
                        <div className="mb-3 inline-block rounded-md bg-[#0a1f2e] px-3 py-1.5 text-sm text-white"><b>{users} phone{users > 1 ? 's' : ''}</b> <span className="text-white/70">{money(unitPrice(planInfo))}/phone</span></div>
                        <input type="range" min="1" max={maxUsers} value={users} onChange={(e) => setUsers(Number(e.target.value))} className="w-full accent-[#0a1f2e]" aria-label="Number of phones" />
                        <div className="flex justify-between text-xs text-gray-400"><span>1</span><span>{maxUsers}</span></div>
                        {!trial && <p className="mt-3 text-xs text-gray-500">Save 5% from 10 phones, 10% from 25 and 15% from 40. More than 50 phones? <a href="mailto:hello@voip2.co.uk" className="underline">Ask us for a quote</a>.</p>}
                      </div>
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h2 className="text-xl font-semibold text-[#0a1f2e]">Let's choose a phone number{maxNumbers > 1 ? ' (or two)' : ''}</h2>
                      <p className="mt-2 text-sm text-gray-500">Choose a national 03 number or a local 01/02 number. {trial ? 'Your trial includes one number.' : `The first number is included${extraPrice ? `; more are ${money(extraPrice)}/month` : ''}.`}</p>
                    </div>
                    <label className="flex gap-4 rounded-xl border border-blue-100 bg-blue-50/60 p-5">
                      <input type="checkbox" className="mt-1 h-5 w-5" checked={migrate} onChange={(e) => setMigrate(e.target.checked)} />
                      <span>
                        <span className="font-semibold text-[#0a1f2e]">Do you have an existing number you want to bring to us?</span>
                        <span className="mt-1 block text-sm text-gray-600">Tick this box and we'll arrange the move once your phone system is set up. You don't have to choose a new number, but you can.</span>
                        {migrate && <input value={portNumber} onChange={(e) => setPortNumber(e.target.value)} placeholder="e.g. 020 7946 0000"
                          className="mt-3 w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2" />}
                      </span>
                    </label>
                    <Panel>
                      <div className="flex flex-wrap border-b text-sm">
                        {[['national', 'National numbers (03)'], ['local', 'Local numbers (01 and 02)']].map(([k, l]) => (
                          <button key={k} type="button" onClick={() => setTab(k)}
                            className={`px-5 py-3 font-medium ${tab === k ? 'border-b-2 border-[#0a1f2e] text-[#0a1f2e]' : 'text-gray-500'}`}>{l}</button>
                        ))}
                        <span className="ml-auto px-5 py-3 text-gray-500">{chosen.length} of {maxNumbers} chosen</span>
                      </div>
                      <div className="p-5">
                        {tab === 'local' && (
                          <select value={area} onChange={(e) => setArea(e.target.value)} className="mb-4 rounded-lg border border-gray-300 px-3 py-2 text-sm">
                            {(options?.areas || []).map((a) => <option key={a.prefix} value={a.prefix}>{a.prefix} · {a.name}</option>)}
                          </select>
                        )}
                        {numbers.loading ? <p className="py-6 text-center text-sm text-gray-500">Finding free numbers…</p>
                          : !numbers.available ? <p className="py-6 text-center text-sm text-gray-500">Number choice is not available right now. Continue and we'll give you a number, or tick the box above.</p>
                            : numbers.list.length === 0 ? <p className="py-6 text-center text-sm text-gray-500">No free numbers in this range right now. Try another area.</p>
                              : (
                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                  {numbers.list.map((n) => {
                                    const on = chosen.includes(n);
                                    return (
                                      <button key={n} type="button" onClick={() => toggleNumber(n)}
                                        className={`rounded-lg border-2 px-3 py-3 text-center transition ${on ? 'border-[#0a1f2e] bg-[#c8ff00]' : 'border-gray-200 hover:border-gray-400'}`}>
                                        <div className="font-mono text-base font-semibold text-[#0a1f2e]">{pretty(n)}</div>
                                        <div className="text-xs text-gray-500">{chosen.indexOf(n) > 0 && extraPrice ? `${money(extraPrice)}/month` : 'Included'}</div>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                        <button type="button" onClick={search} className="mx-auto mt-4 flex items-center gap-1 text-sm text-gray-500 underline"><RefreshCw className="h-3 w-3" />Show other numbers</button>
                      </div>
                    </Panel>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <div className="text-center">
                      <h2 className="text-xl font-semibold text-[#0a1f2e]">How will each person make calls?</h2>
                      <p className="mt-2 text-sm text-gray-500">The web phone and mobile app are included. Choose a desk phone if someone needs one.</p>
                    </div>
                    {phones.map((p, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 p-4">
                        <span className="w-20 text-sm font-semibold text-gray-700">Phone {i + 1}</span>
                        <div className="flex rounded-lg bg-gray-100 p-1 text-sm">
                          <button type="button" onClick={() => setPhone(i, { kind: 'app' })}
                            className={`flex items-center gap-1 rounded-md px-3 py-1.5 ${p.kind === 'app' ? 'bg-white shadow font-medium' : 'text-gray-600'}`}>
                            <Monitor className="h-4 w-4" /><Smartphone className="h-4 w-4" />Webphone / Mobile app</button>
                          <button type="button" onClick={() => setPhone(i, { kind: 'desk' })} disabled={!options?.phones.length}
                            className={`flex items-center gap-1 rounded-md px-3 py-1.5 ${p.kind === 'desk' ? 'bg-white shadow font-medium' : 'text-gray-600'} disabled:opacity-40`}>
                            <Phone className="h-4 w-4" />Desk phone</button>
                        </div>
                        {p.kind === 'app' && <span className="text-xs text-gray-500">Included</span>}
                        {p.kind === 'desk' && (
                          <div className="flex flex-1 flex-wrap gap-2">
                            {(options?.phones || []).map((h) => (
                              <button key={h.id} type="button" disabled={!h.in_stock} onClick={() => setPhone(i, { product_id: h.id })}
                                className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-left text-sm ${p.product_id === h.id ? 'border-[#0a1f2e] bg-[#c8ff00]/40' : 'border-gray-200'} disabled:opacity-40`}>
                                {h.image_url && <img src={h.image_url} alt="" className="h-8 w-8 object-contain" />}
                                <span><b>{h.name}</b><br /><span className="text-xs text-gray-500">{h.in_stock ? `${money(h.price_pence / 100)} one-off` : 'Out of stock'}</span></span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {trial && deskPhones.length > 0 && <p className="text-center text-xs text-gray-500">During the trial you can use the apps straight away. Desk phones are sent when you place your order.</p>}
                  </div>
                )}

                {step === 3 && (
                  <div className="mx-auto max-w-xl space-y-4">
                    <h2 className="text-center text-xl font-semibold text-[#0a1f2e]">Set up your VOIP2 account</h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {input('first_name', 'First name', 'text', { autoComplete: 'given-name' })}
                      {input('last_name', 'Last name', 'text', { autoComplete: 'family-name' })}
                    </div>
                    <CompanyField value={form.company} number={form.company_number} search={publicCompanySearch}
                      onChange={(company, company_number) => setForm((x) => ({ ...x, company, company_number }))} />
                    {input('email', 'Work email', 'email', { autoComplete: 'email' })}
                    {input('phone', 'Contact phone', 'tel', { autoComplete: 'tel' })}
                    <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                      className="hidden" aria-hidden="true" />
                    <ContractSign product="cloud_pbx" company={form.company} companyNumber={form.company_number}
                      value={signature} onChange={setSignature} />
                    <label className="flex items-start gap-3 text-sm text-gray-600">
                      <input type="checkbox" className="mt-1" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} />
                      <span>I agree to the <Link to="/terms-of-service" className="underline" target="_blank">terms</Link> and <Link to="/privacy-policy" className="underline" target="_blank">privacy policy</Link>{trial ? '. The trial includes 100 UK minutes. After 14 days the service continues on a 24-month term if I have added a card; otherwise it is switched off.' : '. The service has a 24-month minimum term.'}</span>
                    </label>
                  </div>
                )}
              </>
            )}
          </div>

          {!done && (
            <div className="flex items-center justify-between border-t bg-gray-50 px-6 py-4 sm:px-10">
              <button type="button" disabled={step === 0} onClick={() => { setErrors([]); setStep(step - 1); }}
                className="flex items-center gap-1 rounded-full px-5 py-2.5 text-sm text-gray-600 disabled:opacity-30"><ArrowLeft className="h-4 w-4" />Back</button>
              {step < 3 ? (
                <button type="button" onClick={next} className="flex items-center gap-1 rounded-full bg-[#0a1f2e] px-6 py-2.5 text-sm font-semibold text-white">
                  {step === 0 ? 'Continue to choose your numbers' : 'Continue'}<ArrowRight className="h-4 w-4" /></button>
              ) : (
                <button type="button" disabled={busy} onClick={submit} className="rounded-full bg-[#c8ff00] px-6 py-2.5 text-sm font-semibold text-[#0a1f2e] disabled:opacity-50">
                  {busy ? 'Setting up…' : trial ? 'Start my free trial' : 'Place my order'}</button>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default FreeTrial;
